import { Checkbox } from "@mui/material";

import { ExplainedControl } from "./ExplainedControl";

/**
 * A tri-state checkbox: checked when everything it covers is on, indeterminate when
 * only some of it is.
 *
 * `aria-checked="mixed"` is set by hand, because MUI marks the indeterminate
 * position with an icon and a `data-indeterminate` attribute and leaves the input's
 * own state alone.
 */

export type PreferenceCheckboxProps = {
  /** The checkbox's accessible name: which category, and which channel. */
  label: string;
  explanation: string;
  /** True only when everything covered is on, so a click from mixed turns all on. */
  checked: boolean;
  /** Set when some but not all of what is covered is on. */
  indeterminate: boolean;
  /** Set while a write is in flight. Never set for a control merely muted from above. */
  disabled?: boolean;
  onChange: (enabled: boolean) => void;
};

export function PreferenceCheckbox({
  label,
  explanation,
  checked,
  indeterminate,
  disabled,
  onChange,
}: PreferenceCheckboxProps) {
  return (
    <ExplainedControl explanation={explanation}>
      {(descriptionId) => (
        <Checkbox
          checked={checked}
          indeterminate={indeterminate}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
          slotProps={{
            input: {
              "aria-label": label,
              "aria-describedby": descriptionId,
              // Only when mixed; the native checked state is accurate otherwise.
              "aria-checked": indeterminate ? "mixed" : undefined,
            },
          }}
        />
      )}
    </ExplainedControl>
  );
}
